import { useEffect, useState } from "react";
import api from "@/lib/api";

const STAGES = [
  { key: "invited", label: "Invitation sent" },
  { key: "opened", label: "Invitation opened" },
  { key: "registered", label: "Registration started" },
  { key: "submitted", label: "Application submitted" },
  { key: "approved", label: "Application approved" },
  { key: "activated", label: "Membership activated" },
];

export default function MyReferrals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/homeowner/referrals");
      setRows(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copyShareLink = async (id) => {
    try {
      const { data } = await api.get(`/homeowner/referrals/${id}/share-url`);
      await navigator.clipboard.writeText(data.share_url);
      setMsg("Link copied. Paste it to your painter via SMS, WhatsApp, or however you normally chat.");
      setTimeout(() => setMsg(""), 4000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to copy link.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="my-referrals-page">
      <header className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">ColourMatch</div>
          <h1 className="text-2xl font-bold text-brand-navy">Invite my painter</h1>
          <p className="text-sm text-gray-600 mt-1">Bring the painter you already trust onto ColourMatch. They&apos;ll get 20% off their first invoice, and you&apos;ll share your project with them directly.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="px-4 py-2 rounded-lg bg-brand-teal text-white font-semibold text-sm hover:bg-brand-teal/90" data-testid="new-invitation-btn">
          + New invitation
        </button>
      </header>

      {msg && <div className="mb-3 p-3 rounded-lg bg-brand-teal/10 border border-brand-teal/40 text-sm text-brand-teal" data-testid="referral-msg">{msg}</div>}

      {loading && <div className="p-6 text-brand-navy">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="p-8 text-center bg-brand-cream border border-brand-border rounded-xl" data-testid="referrals-empty">
          <p className="text-sm text-gray-700 mb-4">No invitations yet.</p>
          <button onClick={() => setShowModal(true)} className="px-4 py-2 rounded-lg bg-brand-navy text-white font-semibold text-sm">Invite your painter</button>
        </div>
      )}

      <div className="space-y-4">
        {rows.map((r) => <ReferralCard key={r.id} r={r} copyLink={() => copyShareLink(r.id)} />)}
      </div>

      {showModal && <InviteModal onClose={() => { setShowModal(false); load(); }} onCopyShare={copyShareLink} />}
    </div>
  );
}

function ReferralCard({ r, copyLink }) {
  const stageIdx = STAGES.findIndex((s) => s.key === r.status);
  const isDeclined = r.status === "declined";
  const isExpired = r.status === "expired";
  return (
    <div className="border border-brand-border rounded-xl p-4 bg-white" data-testid={`referral-card-${r.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-bold text-brand-navy">{r.painter_name}</div>
          <div className="text-xs text-gray-600">{r.painter_email || "invited via link"} · sent {new Date(r.created_at).toLocaleDateString("en-AU")}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
            r.status === "activated" ? "bg-brand-success/15 text-brand-success" :
            isDeclined || isExpired ? "bg-gray-100 text-gray-600" :
            "bg-brand-teal/10 text-brand-teal"
          }`} data-testid={`referral-status-${r.id}`}>
            {r.status.replace(/_/g, " ")}
          </span>
          {r.method === "link" && !isDeclined && !isExpired && (
            <button onClick={copyLink} className="text-xs text-brand-teal hover:underline" data-testid={`copy-link-${r.id}`}>Copy link again</button>
          )}
        </div>
      </div>
      {!isDeclined && !isExpired && (
        <ol className="space-y-1" data-testid={`referral-timeline-${r.id}`}>
          {STAGES.map((s, i) => (
            <li key={s.key} className={`flex items-center gap-2 text-xs ${i <= stageIdx ? "text-brand-navy font-semibold" : "text-gray-400"}`}>
              <span className={`w-2 h-2 rounded-full ${i <= stageIdx ? "bg-brand-teal" : "bg-gray-300"}`}></span>
              {s.label}
              {i === stageIdx && <span className="text-[10px] uppercase font-bold text-brand-teal ml-1">Current</span>}
            </li>
          ))}
        </ol>
      )}
      {isDeclined && <div className="text-xs text-gray-600 italic">Your painter declined this invitation.</div>}
      {isExpired && <div className="text-xs text-gray-600 italic">This invitation expired unused. You can create a new one.</div>}
      {r.discount_applied && (
        <div className="mt-2 text-xs text-brand-success font-semibold" data-testid={`discount-applied-${r.id}`}>
          🎉 Your painter received their {Math.round(r.discount_percent || 20)}% introductory discount.
        </div>
      )}
    </div>
  );
}

function InviteModal({ onClose, onCopyShare }) {
  const [method, setMethod] = useState("email");
  const [painterName, setPainterName] = useState("");
  const [painterEmail, setPainterEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      const payload = { method, painter_name: painterName };
      if (method === "email") payload.painter_email = painterEmail;
      const { data } = await api.post("/homeowner/referrals", payload);
      setCreated(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to create invitation.");
    } finally { setSaving(false); }
  };

  const copyLink = async () => {
    if (!created?.share_url) return;
    try { await navigator.clipboard.writeText(created.share_url); } catch (_) {}
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="invite-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        {!created ? (
          <>
            <h2 className="text-xl font-bold text-brand-navy mb-1">Invite your painter</h2>
            <p className="text-sm text-gray-600 mb-4">Bring the painter you already trust onto ColourMatch Pro. They&apos;ll get 20% off their first invoice.</p>
            <div className="flex gap-2 mb-4">
              {["email", "link"].map((m) => (
                <button key={m} onClick={() => setMethod(m)} className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${method === m ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-brand-navy border-brand-border"}`} data-testid={`invite-method-${m}`}>
                  {m === "email" ? "Send by email" : "Copy a shareable link"}
                </button>
              ))}
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-brand-navy mb-1">Painter&apos;s name</span>
                <input value={painterName} onChange={(e) => setPainterName(e.target.value)} className="w-full px-3 py-2 border border-brand-border rounded-lg text-sm" placeholder="e.g. Sarah from Sarah's Painting Co" data-testid="invite-painter-name" />
              </label>
              {method === "email" && (
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-brand-navy mb-1">Painter&apos;s email</span>
                  <input type="email" value={painterEmail} onChange={(e) => setPainterEmail(e.target.value)} className="w-full px-3 py-2 border border-brand-border rounded-lg text-sm" placeholder="painter@theirdomain.com" data-testid="invite-painter-email" />
                </label>
              )}
            </div>
            {err && <div className="mt-3 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="invite-err">{String(err)}</div>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-brand-border text-brand-navy font-semibold text-sm">Cancel</button>
              <button onClick={submit} disabled={saving || !painterName || (method === "email" && !painterEmail)} className="px-4 py-2 rounded-lg bg-brand-teal text-white font-semibold text-sm disabled:opacity-50" data-testid="invite-submit">
                {saving ? "Creating…" : method === "email" ? "Send invitation" : "Get shareable link"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-4xl mb-2">🎨</div>
            <h2 className="text-xl font-bold text-brand-navy mb-1">Invitation ready</h2>
            {method === "email" ? (
              <p className="text-sm text-gray-700 mb-4">We&apos;ve sent <strong>{painterName}</strong> an email invitation. When they join, you&apos;ll see their status update here.</p>
            ) : (
              <>
                <p className="text-sm text-gray-700 mb-3">Copy this link and send it to <strong>{painterName}</strong> via SMS, WhatsApp, Messenger, or however you normally chat.</p>
                <div className="p-2 bg-brand-cream rounded border border-brand-border text-xs font-mono break-all mb-3" data-testid="invite-share-url">{created.share_url}</div>
                <button onClick={copyLink} className="w-full py-2 rounded-lg bg-brand-teal text-white font-semibold text-sm mb-3" data-testid="invite-copy-btn">Copy link</button>
              </>
            )}
            <div className="mt-4 flex justify-end">
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-brand-navy text-white font-semibold text-sm" data-testid="invite-done-btn">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
